import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import ProductIssue from '@/models/Products/ProductIssues/ProductIssue';
import { AppModule } from '@/store/Auth/AppModule';
import ProductType from '@/models/Products/ProductType';
import ProductSubType from '@/models/Products/ProductSubType';
import ProductIssueState from '@/models/Products/ProductIssues/ProductIssueState';
import PermissionUtils from '@/lib/permissionUtils';

export interface IProductIssueListState {
    loading: boolean;
    productIssues: ProductIssue[];
    totalProductIssuesPages: number;
    filterByProductType: ProductType;
    filterByProductSubType: ProductSubType;
    filterByProductIssueState: ProductIssueState;
    pageSize: number;
}

@Module({ dynamic: true, store, name: 'ProductIssueListModule', namespaced: true })
class ProductIssueListStore extends VuexModule implements IProductIssueListState {

    public loading: boolean = true;
    public productIssues: ProductIssue[] = [];
    public totalProductIssuesPages: number = 1;
    public filterByProductType: ProductType = null;
    public filterByProductSubType: ProductSubType = null;
    public filterByProductIssueState: ProductIssueState = null;
    public pageSize: number = PermissionUtils.getPageSize();

    @Action
    async getProductIssues(pageNumber: number) {

        this.setLoading(true);

        const productTypeParameter = this.filterByProductType ? `&type=${this.filterByProductType}` : ``;
        const productSubTypeParameter = this.filterByProductSubType ? `&subType=${this.filterByProductSubType}` : ``;
        const stateParameter = this.filterByProductIssueState ? `&state=${this.filterByProductIssueState}` : ``;

        await ajax.get(`/portal-backend/fi-api/internal/product-issues?pageSize=${this.pageSize}&pageNumber=${pageNumber}${productTypeParameter}${productSubTypeParameter}${stateParameter}`)
        .then(response => {
            this.context.commit("setProductIssues", response.data.results);
            this.context.commit("setProductIssuesTotalPages", response.data.records.totalRecords);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    updateProductTypeFilter(productTypeFilter: ProductType) {
        this.context.commit("setFilterByProductType", productTypeFilter);
    }

    @Action
    updateProductSubTypeFilter(productSubTypeFilter: ProductSubType) {
        this.context.commit("setFilterByProductSubType", productSubTypeFilter);
    }

    @Action
    updateProductIssueStateFilter(productIssueStateFilter: ProductIssueState) {
        this.context.commit("setFilterByProductIssueState", productIssueStateFilter);
    }

    @Action
    clearAllFilters() {
        this.context.commit("setFilterByProductType", null);
        this.context.commit("setFilterByProductSubType", null);
        this.context.commit("setFilterByProductIssueState", null);
    }

    @Action
    cleanState() {
        this.context.commit("resetState");
    }

    @Mutation
    resetState() {
        this.loading = true;
        this.productIssues = [];
        this.totalProductIssuesPages = 1;
        this.filterByProductType = null;
        this.filterByProductSubType = null;
        this.filterByProductIssueState = null;
    }

    @Mutation
    private async setLoading(loading: boolean) {
        this.loading = loading;
    }

    @Mutation
    private async setProductIssues(response: ProductIssue[]) {
        if (response) {
            this.productIssues = response;
        }
    }

    @Mutation
    private async setProductIssuesTotalPages(total: number) {
        this.totalProductIssuesPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setFilterByProductType(filter: ProductType) {
        this.filterByProductType = filter;
    }

    @Mutation
    private async setFilterByProductSubType(filter: ProductSubType) {
        this.filterByProductSubType = filter;
    }

    @Mutation
    private async setFilterByProductIssueState(filter: ProductIssueState) {
        this.filterByProductIssueState = filter;
    }

}

export const ProductIssueListModule = getModule(ProductIssueListStore);